import type { ReactNode } from 'react';
import { Button, PicturePlaceholder } from '../../modules/design-system';

export interface EmptyStateProps {
  title: string;
  description?: string;
  cta?: string;
  onAction?: () => void;
  /**
   * Override the default illustration. Defaults to a glass-disc picture
   * placeholder. Pass `null` to skip the illustration block entirely.
   */
  icon?: ReactNode | null;
}

/**
 * Empty-state block for the My Packs tabs (Repackz / Cards / Deliveries).
 * Themed via Design Constructor (`--co-MyPacksEmptyState-*`) — every
 * visible colour and the title size flow through CSS variables so an
 * operator can re-skin the block from the console. The CTA button keeps
 * the shared `MyPacksToolbarSubmit` palette so its gloss matches the
 * Buyback / Rip-now buttons.
 */
export function EmptyState({ title, description, cta, onAction, icon }: EmptyStateProps) {
  const showIcon = icon !== null;
  return (
    <div
      data-co-component="MyPacksEmptyState"
      className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center"
    >
      {showIcon && (
        <div
          aria-hidden
          className="flex size-20 items-center justify-center rounded-full"
          style={{
            // `padding-box` clip + `box-shadow inset` ring (instead of
            // `border`) avoids the gradient bleeding into the 1px border
            // strip — that's what painted the green/red crescents at the
            // disc edges with vivid operator colours.
            backgroundImage:
              'linear-gradient(180deg, var(--co-MyPacksEmptyState-iconBgFrom, rgba(255,255,255,0.10)) 0%, ' +
              'var(--co-MyPacksEmptyState-iconBgTo, rgba(255,255,255,0.04)) 100%)',
            backgroundClip: 'padding-box',
            boxShadow:
              'inset 0 0 0 1px var(--co-MyPacksEmptyState-iconBorder, rgba(255,255,255,0.10))',
            color: 'var(--co-MyPacksEmptyState-iconColor, rgba(255,255,255,0.55))',
          }}
        >
          {icon ?? <PicturePlaceholder size={32} strokeWidth={2} />}
        </div>
      )}

      <h3
        className="font-bold  tracking-[-0.36px]"
        style={{
          color: 'var(--co-MyPacksEmptyState-titleColor, #FFFFFF)',
          fontSize: 'var(--co-MyPacksEmptyState-titleSize, 18px)',
          lineHeight: '24px',
        }}
      >
        {title}
      </h3>

      {description && (
        <p
          className="max-w-[320px]"
          style={{
            color: 'var(--co-MyPacksEmptyState-descriptionColor, rgba(255,255,255,0.60))',
            fontSize: '13px',
            lineHeight: '22px',
            letterSpacing: '-0.2px',
          }}
        >
          {description}
        </p>
      )}

      {cta && onAction && (
        <div className="mt-2">
          <Button variant="primary" size="md" onClick={onAction}>
            {cta}
          </Button>
        </div>
      )}
    </div>
  );
}
